/**
 * Reorders array X based on the order of elements in Y.
 * Elements that appear in Y are placed first, in Y's order.
 * The rest of the elements are inserted based on their previous element in X.
 * If no previous element is found in the result, they are placed at the start.
 *
 * @template T
 * @param {T[]} X 
 * @param {T[]} Y 
 * @returns {T[]} 
 * 
 * @example
 * mergeInOrder([1, 2, 3], [1, 2, 3]);
 * // -> [1, 2, 3]
 * 
 * @example
 * mergeInOrder([1, 2, 3], [3, 2, 1]);
 * // -> [3, 2, 1]
 * 
 * @example
 * mergeInOrder([1, 2, 3], [3, 1]);
 * // -> [3, 1, 2]
 * 
 * @example
 * mergeInOrder([1, 2, 3, 4], [3, 1]);
 * // -> [3, 4, 1, 2]
 *
 * @example
 * mergeInOrder(['a', 'b', 'c', 'd', 'e'], ['d', 'b']);
 * // -> ['d', 'b', 'a', 'c', 'e']
 *
 * @example
 * mergeInOrder(['apple', 'banana', 'cherry', 'date'], ['date', 'apple']);
 * // -> ['date', 'apple', 'banana', 'cherry']
 */
function mergeInOrder(X, Y) {
  if (X === undefined || Y === undefined) 
    return X;

  const ySet = new Set(Y);
  const result = [];

  for (const y of Y) {
    if (X.includes(y)) {
      result.push(y);
    }
  }

  for (const x of X) {
    if (!ySet.has(x)) {
      const idxInX = X.indexOf(x);
      let inserted = false;

      for (let i = idxInX - 1; i >= 0; i--) {
        const prev = X[i];
        const pos = result.indexOf(prev);
        if (pos !== -1) {
          result.splice(pos + 1, 0, x);
          inserted = true;
          break;
        }
      }

      if (!inserted) {
        result.unshift(x);
      }
    }
  }

  return result;
}

export { mergeInOrder };